const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const ACCESS_KEY = "nexora_access";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | undefined,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function hasSession(): boolean {
  return Boolean(localStorage.getItem(ACCESS_KEY));
}

export function saveSession(accessToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
}

let refreshPromise: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const response = await safeFetch(`${API_URL}/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) return false;
  const data = await response.json() as { accessToken: string };
  saveSession(data.accessToken);
  return true;
}

function refresh(): Promise<boolean> {
  refreshPromise ??= performRefresh().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const accessToken = localStorage.getItem(ACCESS_KEY);
  const hasBody = init.body !== undefined && init.body !== null;
  const response = await safeFetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...init.headers,
    },
  });
  if (response.status === 401 && retry && await refresh()) return api<T>(path, init, false);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "NETWORK_ERROR" })) as { error?: string; message?: string; fields?: Array<{ path: string; message: string }> };
    const validation = error.fields?.map((field) => field.message).join("; ");
    throw new ApiError(
      error.message ?? validation ?? customerError(error.error, response.status),
      response.status,
      error.error,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const customerError = (code: string | undefined, status: number) => ({
  DEVICE_SLOT_LIMIT: "Достигнут максимальный лимит устройств",
  NOT_ACTIVE: "Подписка не активна",
  INTERNAL_ERROR: "Сервис временно не завершил операцию. Повторите через несколько секунд.",
  NETWORK_ERROR: "Не удалось получить ответ сервера",
} as Record<string, string>)[code ?? ""] ?? code ?? `Ошибка HTTP ${status}`;

async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(15_000) });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    if (cause instanceof DOMException && cause.name === "TimeoutError") {
      throw new Error("Сервер не ответил вовремя. Повторите через несколько секунд.");
    }
    throw new Error("Нет связи с NexoraVPN. Проверьте интернет или VPN и повторите.");
  }
}

export async function logoutSession(): Promise<void> {
  try {
    await api<void>("/v1/auth/logout", { method: "POST" });
  } catch {
    // Local logout must still succeed if the network or session is unavailable.
  } finally {
    clearSession();
  }
}
